import { useEmpresasReceita } from "../../lib/receitas/hooks";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Seletor de empresa da receita manual (Tela 5 §10.5 — leitura).
 *
 * Espelha `conciliacao/SeletorEmpresaConciliacao`, mas é uma peça PRÓPRIA da
 * receita: o seletor da conciliação consome `useEmpresasConciliaveis` (escopo
 * `CONCILIAR`), que é a lista ERRADA para receita — um usuário que pode LANÇAR
 * receita mas não conciliar veria a lista errada. Receita manual é SEMPRE por
 * empresa (não há escopo de grupo), e a lista vem filtrada pelo escopo de
 * `criar_receita_manual` no backend (`GET /api/receitas/empresas`).
 *
 * Rótulo de cada empresa: `fantasia ?? razao_social`. ESCOPO VAZIO (`[]`) →
 * estado claro, não um `<select>` vazio confuso.
 */

interface SeletorEmpresaReceitaProps {
  /** Id da empresa selecionada, ou `null` enquanto nada foi escolhido. */
  selecionada: string | null;
  /** Notifica a página da empresa escolhida. */
  aoSelecionar: (empresaId: string) => void;
}

export function SeletorEmpresaReceita({
  selecionada,
  aoSelecionar,
}: SeletorEmpresaReceitaProps): JSX.Element {
  const consulta = useEmpresasReceita();

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando empresas…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as empresas.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const empresas = consulta.data;

  // ESCOPO VAZIO: nada a lançar — estado claro, não um seletor vazio.
  if (empresas.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Você não tem empresas para lançar receita.
      </p>
    );
  }

  function aoMudar(evento: React.ChangeEvent<HTMLSelectElement>): void {
    const valor = evento.target.value;
    if (valor === "") {
      return;
    }
    aoSelecionar(valor);
  }

  return (
    <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
      <span className="font-subtitulo">Empresa</span>
      <select
        value={selecionada ?? ""}
        onChange={aoMudar}
        className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
      >
        <option value="" disabled>
          Selecione…
        </option>
        {empresas.map((empresa) => (
          <option key={empresa.id} value={empresa.id}>
            {empresa.fantasia ?? empresa.razao_social}
          </option>
        ))}
      </select>
    </label>
  );
}
